import {
    Box,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
} from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import Icon from '@apigrowfinance/pbolt-icon';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { RiAccountCircleLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { TransactionItem } from '@clients/transactionsClient';
import TimelineDots from '@components/accountStatements/TimelineDots';
import TwoCurrencyTransaction from '@components/twoCurrencyTransaction/TwoCurrencyTransaction';

export type StatementTransactionProps = {
    transaction: TransactionItem;
    accountId: string;
};

const StatementTransaction = ({
    transaction,
    accountId,
}: StatementTransactionProps) => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const principalTransaction = transaction?.amount?.find(
        (a) => a?.type === 'PRINCIPAL',
    );
    const localTransaction = transaction?.amount?.find(
        (a) => a?.type === 'LOCAL',
    );
    const isLocalTransaction =
        localTransaction?.value == null ||
        principalTransaction?.value === localTransaction?.value;

    return (
        <ListItemButton
            sx={{
                width: '100%',
                display: 'block',
                pt: 0,
                pb: 0,
            }}
            onClick={() => {
                if (accountId == null || transaction.id == null) {
                    return;
                }
                navigate(`/${accountId}/statements/${transaction.id}`);
            }}>
            <ListItem
                sx={{ pl: 0, pt: 0, pb: 0 }}
                secondaryAction={
                    <ListItemText
                        primaryTypographyProps={{
                            style: {
                                textAlign:
                                    theme.direction === 'rtl'
                                        ? 'left'
                                        : 'right',
                            },
                        }}
                        primary={
                            isLocalTransaction ? (
                                <CurrencyFormatter
                                    testId="statement-balance"
                                    currency={principalTransaction?.currency}
                                    amount={principalTransaction?.value}
                                    symbol={
                                        transaction.transaction_type.credit
                                            ? 'positive'
                                            : 'default'
                                    }
                                    variant={
                                        transaction.transaction_type.credit
                                            ? 'success'
                                            : 'default'
                                    }
                                />
                            ) : (
                                <TwoCurrencyTransaction
                                    principalTransaction={principalTransaction}
                                    localTransaction={localTransaction}
                                    symbol={
                                        transaction.transaction_type.credit
                                            ? 'positive'
                                            : 'default'
                                    }
                                    variant={
                                        transaction.transaction_type.credit
                                            ? 'success'
                                            : 'default'
                                    }
                                />
                            )
                        }
                        secondaryTypographyProps={{
                            style: {
                                textAlign:
                                    theme.direction === 'rtl'
                                        ? 'left'
                                        : 'right',
                            },
                        }}
                        secondary={
                            transaction.transaction_type.credit ? (
                                <FormattedMessage
                                    id="AccountStatements.credit"
                                    defaultMessage="Credit"
                                />
                            ) : (
                                <FormattedMessage
                                    id="AccountStatements.debit"
                                    defaultMessage="Debit"
                                />
                            )
                        }
                    />
                }>
                <TimelineDots />
                <ListItemAvatar>
                    <Icon data-testid="bill-icon">
                        <RiAccountCircleLine />
                    </Icon>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        transaction?.merchant?.name ||
                        transaction?.soft_descriptor
                    }
                    secondary={
                        <>
                            <DateFormatter
                                date={transaction?.event_date}
                                format="shortDateWithTimeArb"
                            />
                            {transaction.merchant?.category?.description && (
                                <Box component="span">
                                    {' • '}
                                    {
                                        transaction.merchant?.category
                                            ?.description
                                    }
                                </Box>
                            )}
                        </>
                    }
                />
            </ListItem>
        </ListItemButton>
    );
};

export default StatementTransaction;
